import { Howl, Howler } from 'howler';
import { config } from '../config/config';
import { isIOS } from './features';
import { itemsConfig } from '../config/itemsConfig';
import { EVENT_ACTIVITY_HOVER_INFO, sendCustomEvent } from './stats';

let isMuted: boolean = false;
let itemSfx: Howl | null;
let sfx: Howl | null;
let preventSounds: boolean = false;

const sfxs: { [key: string]: { count: number; backgroundMusicVolume?: number; actual: number } } = {};
Object.keys(itemsConfig).map(
  (key: string) =>
    (sfxs[key] = {
      count: itemsConfig[key].numSounds,
      backgroundMusicVolume: itemsConfig[key].backgroundMusicVolume,
      actual: 0,
    }),
);

// music, speech, ambi
const currentSounds: { [key: string]: any } = {};
const soundPool: { [key: string]: any } = {};

export let musicEnabled = true; // global music switch button

export const initSound = () => {
  Howler.volume(config.sound.GLOBAL_VOLUME);
  setAutoMute();
};

export const enableMusic = (enable: boolean) => {
  musicEnabled = enable;

  for (const groupName in currentSounds) {
    const soundInfo = currentSounds[groupName];
    if (soundInfo && soundInfo.canBeMuted) {
      if (enable) {
        if (!soundInfo.sound.playing()) {
          soundInfo.sound.play();
        }
      } else {
        soundInfo.sound.pause();
      }
    }
  }

  localStorage.setItem(config.sound.LOCAL_STORAGE_MUSIC, String(musicEnabled));
};

const setAutoMute = () => {
  document.addEventListener(
    'visibilitychange',
    () => {
      Howler.mute(isMuted || document.hidden);
    },
    false,
  );
};

//fade in/out "canBeMuted" marked groups when over FADE item
const tryMute = (doMute: boolean, mutedVolume = config.sound.FADE_VOLUME) => {
  for (const groupName in currentSounds) {
    const soundInfo = currentSounds[groupName];
    if (soundInfo && soundInfo.canBeMuted) {
      if (doMute) {
        soundInfo.sound.fade(soundInfo.sound.volume(), mutedVolume, 350);
        if (isIOS) {
          soundInfo.sound.volume(mutedVolume);
        }
      } else {
        soundInfo.sound.fade(soundInfo.sound.volume(), soundInfo.volume || 1, 500);
        if (isIOS) {
          soundInfo.sound.volume(soundInfo.volume || 1);
        }
      }
    }
  }
};

export const playMusic = (
  name: 'atmo_loop' | 'background_music',
  doPlay = true,
  sndConfig?: { [key: string]: any },
) => {
  if (doPlay) {
    const sound = startSound('music', name, sndConfig);
    //musicSound = sound;
    return sound;
  }
  stopSound('music');
};

export const playAmbient = (name: 'atmo_loop' | 'background_music', doPlay = true) => {
  if (doPlay) {
    startSound('ambient', name, { html5: true });
    //ambientSound = sound;
  } else {
    stopSound('ambient');
  }
};

export const stopSound = (groupName: string) => {
  if (currentSounds[groupName]) {
    currentSounds[groupName].sound.stop();
    currentSounds[groupName] = null;
  }
};

export const startSound = (
  groupName: 'ambient' | 'music',
  soundName: 'atmo_loop' | 'background_music',
  sndConfig = {},
) => {
  stopSound(groupName);

  const soundIdentifier = `${groupName}_${soundName}`;
  let soundInfo = soundPool[soundIdentifier];
  if (soundInfo) {
    currentSounds[groupName] = soundInfo;
    if (!soundInfo.canBeMuted || musicEnabled) {
      if (!soundInfo.sound.playing()) {
        soundInfo.sound.play();
      }
      soundInfo.sound.fade(0, soundInfo.volume || 1, 500);
    }
    return soundInfo.sound;
  }

  if (groupName === 'ambient') {
    soundInfo = {
      ...config.sound.SOUNDS[groupName].globalConfig,
      ...config.sound.SOUNDS[groupName].items['atmo_loop'],
      ...sndConfig,
    };
  } else if (groupName === 'music') {
    soundInfo = {
      ...config.sound.SOUNDS[groupName].globalConfig,
      ...config.sound.SOUNDS[groupName].items['background_music'],
      ...sndConfig,
    };
  }

  const sound = new Howl({
    src: [
      `${config.sound.SOUNDS[groupName].path}${soundName}.mp3`,
      `${config.sound.SOUNDS[groupName].path}${soundName}.ogg`,
    ],
    autoplay: false,
    loop: !!soundInfo.loop,
    volume: soundInfo.volume || 1,
    html5: !!soundInfo.html5,
  });
  soundInfo.sound = sound;

  soundPool[`${groupName}_${soundName}`] = soundInfo;
  currentSounds[groupName] = soundInfo;
  if (!(soundInfo.canBeMuted && !musicEnabled)) {
    sound.play();
  }
  return sound;
};

export const playSfx = (sfxName: string, volume?: number) => {
  const snd = new Howl({
    src: [`${config.sound.SFX_PATH}${sfxName}.mp3`, `${config.sound.SFX_PATH}${sfxName}.ogg`],
  });
  if (volume) {
    snd.volume(volume);
  }
  if (!snd.playing()) {
    snd.play();
  }
  sfx = snd;

  if (sfxName === 'teta_outro_01' || sfxName === 'teta_outro_02' || sfxName === 'teta_after_outro') {
    preventSounds = true;

    snd.once('end', () => {
      preventSounds = false;
    });
  }

  return snd;
};

export const playObjectSfx = (sfxName: string, index = 0) => {
  preventSounds = true;
  const snd = new Howl({
    src: [`${config.sound.OBJECTS_PATH}${sfxName}_${index}.mp3`, `${config.sound.OBJECTS_PATH}${sfxName}_${index}.ogg`],
  });
  snd.play();
  return snd;
};

export const playItemSfx = (itemName: string, onComplete: (() => void) | null) => {
  if (preventSounds) {
    return;
  }
  if (sfx?.playing()) {
    return;
  }
  if (!sfxs[itemName]) {
    throw new Error(`===SOUND==== sfx not in config ${itemName}`);
  }
  stopItemSfx(true);

  const itemSfxName = `${itemName}_${sfxs[itemName].actual}`;
  const nextIndex = (sfxs[itemName].actual + 1) % sfxs[itemName].count;
  sfxs[itemName].actual = nextIndex;

  sendCustomEvent(EVENT_ACTIVITY_HOVER_INFO, `${itemName} - ${sfxs[itemName].actual}`);

  itemSfx = new Howl({
    html5: true,
    src: [`${config.sound.ITEMS_PATH}${itemSfxName}.mp3`, `${config.sound.ITEMS_PATH}${itemSfxName}.ogg`],
  });
  itemSfx.once('end', () => {
    if (onComplete) {
      onComplete();
    }
    tryMute(false);
  });

  itemSfx.play();

  const mutedVolume =
    sfxs[itemName] && sfxs[itemName].backgroundMusicVolume !== null
      ? sfxs[itemName].backgroundMusicVolume
      : config.sound.FADE_VOLUME;
  tryMute(true, mutedVolume);

  return itemSfx;
};

export const playItemSfxAfterTransformation = (itemName: string, onComplete: (() => void) | null) => {
  stopItemSfx(true);
  stopSfx();

  preventSounds = true;
  if (!sfxs[itemName]) {
    throw new Error(`===SOUND==== sfx not in config ${itemName}`);
  }

  const itemSfxName = `${itemName}_${sfxs[itemName].actual}`;
  const nextIndex = (sfxs[itemName].actual + 1) % sfxs[itemName].count;
  sfxs[itemName].actual = nextIndex;

  itemSfx = new Howl({
    html5: true,
    src: [`${config.sound.ITEMS_PATH}${itemSfxName}.mp3`, `${config.sound.ITEMS_PATH}${itemSfxName}.ogg`],
  });
  itemSfx.once('end', () => {
    if (onComplete) {
      onComplete();
    }
    tryMute(false);
    preventSounds = false;
    itemSfx = null;
  });
  itemSfx.on('stop', () => {
    preventSounds = false;
    itemSfx = null;
  });

  itemSfx.play();

  const mutedVolume =
    sfxs[itemName] && sfxs[itemName].backgroundMusicVolume !== null
      ? sfxs[itemName].backgroundMusicVolume
      : config.sound.FADE_VOLUME;
  tryMute(true, mutedVolume);

  return itemSfx;
};

export const stopItemSfx = (immediate?: boolean) => {
  if (immediate) {
    itemSfx?.stop();
    itemSfx = null;
  }
  if (preventSounds) {
    return;
  }
  if (itemSfx) {
    itemSfx.fade(1, 0, 700);
    itemSfx.on('fade', function (this: Howl) {
      this.stop();
    });
    itemSfx = null;

    tryMute(false);
  }
};

// mute ALL sounds when activity is opened
export const setMuted = (muted: boolean) => {
  isMuted = muted;
  // if (muted) {
  //   if (musicSound) {
  //     musicVolume = musicSound.volume();
  //     musicSound.volume(0);
  //   }
  //   if (ambientSound) {
  //     ambientVolume = ambientSound.volume();
  //     ambientSound.volume(0);
  //   }
  // } else {
  //   if (musicSound) {
  //     musicSound.volume(musicVolume);
  //   }
  //   if (ambientSound) {
  //     ambientSound.volume(ambientVolume);
  //   }
  // }
  Howler.mute(muted);
};

export const stopSfx = () => {
  sfx?.stop();
};
